import { z } from 'zod';
import {
  DASHBOARD_SHORTCUT_TARGETS,
  DASHBOARD_TILES,
  DASHBOARD_TILE_IDS,
  LAYOUT_RESOLUTION_SOURCES,
  LOCALES,
  DEFAULT_LOCALE,
  DASHBOARD_ACCESS_ROLES,
  ORG_UNIT_TYPES,
  TILE_SIZES,
} from '../constants';
import { objectIdSchema } from './common.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE DASHBOARD CONFIGURATOR'S WIRE FORMAT. [P6b]
 *
 *  A SEPARATE FILE FROM `dashboard.schema.ts`, mirroring the split the constants already
 *  make: `dashboardTile.constant.ts` declares what a tile IS and `dashboardLayout.constant.ts`
 *  decides which ones a dashboard holds. That one is what a VIEWER receives; this is what a
 *  SUPER ADMIN sends and reads back. They are gated on different capabilities and one of them
 *  runs fourteen aggregations, so keeping them in one file would mean every future change to
 *  the fan-out had to reason about a request that runs none.
 *
 *  ⚠️ DELIBERATELY NOT NAMED `update…Schema`. `updateSchema.test.ts` auto-collects every
 *  export matching `/^update[A-Z].*Schema$/` and asserts it refuses `{}` — free coverage, and
 *  the wrong contract. This is a WHOLE-RESOURCE REPLACE, and borrowing that name invites the
 *  next reader to `.partial()` a schema whose entire meaning is "this is the complete new
 *  state". The "refuses an empty body" claim ships as its own test instead.
 *
 *  ⚠️ AND THE TARGET IS IN THE PATH, NEVER IN THE BODY. `(accessRole, orgUnitType)` names
 *  WHOSE dashboard is being composed, and a path and a body that can disagree about that is
 *  [P5c]'s `dutyEvent` rule — its assignment-keyed routes carry no subject parameter at all,
 *  for exactly this reason. `DASHBOARD_ACCESS_ROLES` rather than `ACCESS_ROLES`, so `NONE`
 *  is not addressable: an officer with no dashboard access has no layout to compose.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

// ── Bounds ───────────────────────────────────────────────────────────────────────

/**
 * ⚠️ A TILE HEADING, NOT A PARAGRAPH. `TileCard` renders the title in an `<h3>` inside a card
 * that is 320px wide at its narrowest (`TILE_MIN_PX`), so a long title does not truncate —
 * §8's rule is that cells never truncate — it WRAPS, and pushes the number out of the card.
 * Sixty characters is about two lines at that measure.
 */
export const DASHBOARD_TILE_TITLE_MAX = 60;

/** The drill label sits on one line at the foot of a card, under the same measure. */
export const DASHBOARD_TILE_DRILL_LABEL_MAX = 40;

// ── The target ───────────────────────────────────────────────────────────────────

export const dashboardLayoutTargetSchema = z.object({
  accessRole: z.enum(DASHBOARD_ACCESS_ROLES),
  orgUnitType: z.enum(ORG_UNIT_TYPES),
});

export type DashboardLayoutTarget = z.infer<typeof dashboardLayoutTargetSchema>;

/**
 * ⚠️ ONE LOCALE PER REQUEST, AND THE STORAGE IS STILL PER LOCALE.
 *
 * §3d asks that overrides be STORED per locale from the first write, and they are — the
 * columns are keyed `<TILE_ID>#<locale>`. What the WIRE carries is one locale's worth,
 * because an editor edits one language at a time and a payload carrying every locale would
 * make a Bengali translator's save able to clobber the English an operator typed.
 *
 * ⚠️ WHICH MAKES THE WRITE A MERGE ACROSS LOCALES AND A REPLACE WITHIN ONE. The service says
 * so at its own call site; it is the one place this contract could silently destroy data.
 *
 * ⚠️ AND THIS IS WHY THERE IS NO `z.record(z.enum(LOCALES), …)` ANYWHERE. MEASURED on zod
 * 4.4.3: that form is EXHAUSTIVE — with `['en','bn']` it REFUSES `{ en: 'x' }`, requiring
 * every locale to be present. With today's single-member tuple it passes, so it would ship
 * green and refuse every partial override the day Bengali lands. `z.partialRecord` is the
 * form that means what it looks like.
 */
export const dashboardLayoutConfigQuerySchema = z.object({
  locale: z.enum(LOCALES).default(DEFAULT_LOCALE),
});

export type DashboardLayoutConfigQuery = z.infer<typeof dashboardLayoutConfigQuerySchema>;

// ── The write ────────────────────────────────────────────────────────────────────

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [amended C1e §3] THE HINT IS OVERRIDABLE NOW, AND THAT REVERSES [P6b] §3c.
 *
 *  This header used to say "there are exactly FOUR keys here, and the absence of a fifth is
 *  the enforcement of §3c" — the caveat was held by a SHAPE WITH NO KEY FOR IT,
 *  `lib/policyPreview.ts`'s guarantee. There are five now, on a client decision.
 *
 *  What has NOT changed, and is the reason the reversal is affordable:
 *
 *   • `web/src/lib/labels.ts` still holds the registry sentence, and it is still where a
 *     number's BOUNDARY is stated — "within your scope only", "command roles are exempt and
 *     are not counted", "from the officer's own handset clock". [P6a] wrote those because a
 *     fairness metric that does not state its own boundary becomes an unfairness generator.
 *   • The CONFIGURATOR and the tile LIBRARY still render that registry sentence, from
 *     `DASHBOARD_TILE_LABEL` DIRECTLY rather than through `resolveTileWords` — so an operator
 *     who hides the line on the card still reads, at rest and beside the field, the sentence
 *     their title has to live with.
 *   • `REQUIREMENT_FULFILMENT`'s caveat IS its `scopes` constraint, which no override moves.
 *
 *  ⚠️ AND THE RESIDUAL RISK IS NOW LARGER RATHER THAN GONE. An operator could always write a
 *  TITLE the caveat contradicts; they can now remove the caveat as well. §3b's answer is that
 *  the screen SAYS SO before the save — clearing the title restores the registry name,
 *  clearing this HIDES THE LINE — because a control whose two fields clear differently and
 *  does not say which is a control that surprises somebody either way.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ A LABEL. BLANK IS REFUSED. A heading with no text is a defect nobody can report except
 * as "the tile has no name", so `''` is a 422 and `null` — a cleared field — means "use the
 * registry's own word".
 */
const labelOverride = (max: number) => z.string().trim().min(1).max(max).nullable();

/**
 * ⚠️ A LINE THAT MAY BE TURNED OFF, AND `''` IS A **THIRD STATE** RATHER THAN A BLANK.
 *
 * The one thing that differs from `labelOverride` is the missing `.min(1)`, which is not
 * legible on its own — hence two named builders one word apart, so a reader can see which
 * rule a field is under without counting method calls.
 *
 *   null  → the registry's own sentence   (never set)
 *   ''    → the line is ABSENT entirely   (§3b — not blank, not a gap)
 *   'x'   → 'x'
 */
const suppressibleOverride = (max: number) => z.string().trim().max(max).nullable();

/**
 * ⚠️ DERIVED, NOT CHOSEN. The longest registry hint is 84 characters, and 120 is about two
 * lines against the same 320px `TILE_MIN_PX` measure `DASHBOARD_TILE_TITLE_MAX` reasons from.
 */
export const DASHBOARD_TILE_HINT_MAX = 120;
/**
 * ⚠️ [P6b.2] A CEILING ON ROWS, NOT ON TILES.
 *
 * The tile count is bounded by the catalogue and by what a repeatable tile is worth placing;
 * rows are a number an operator types, and an unbounded one is a layout that renders a
 * thousand empty groups. Twenty is far past any arrangement anybody would draw and small
 * enough that the grouping stays a grid rather than a list.
 */
export const DASHBOARD_MAX_ROWS = 20;

/**
 * ⚠️ [P6b.2] HOW MANY PLACEMENTS ONE LAYOUT MAY HOLD.
 *
 * It was "one of everything" while a tile id was unique on a page. A repeatable tile removes
 * that ceiling, so one is stated: enough for every number plus a generous set of doors, and
 * a bound somebody can read rather than a page that renders until the browser gives up.
 */
export const DASHBOARD_MAX_PLACEMENTS = 40;

export const dashboardLayoutEntryInputSchema = z.object({
  /**
   * ⚠️ [P6b.2] THE PLACEMENT'S OWN IDENTITY, MINTED BY THE CLIENT AND NEVER RECOMPUTED.
   *
   * §7's identity-not-position rule, fifth instance after `slot_key`, `fillRef` and
   * `duty_point_id` — and here it earns it twice over: a repeatable tile has several
   * placements that a tile id cannot tell apart, and the words and destination are stored
   * against it. A positional key would re-point every override the moment a row was inserted
   * above another. The web mints with `crypto.getRandomValues` (never `randomUUID`, which is
   * secure-context-only); the server fills a missing one from `randomBytes`.
   */
  instanceId: z.string().trim().min(1).max(64),
  id: z.enum(DASHBOARD_TILE_IDS),
  /**
   * ⚠️ [P6b.2] WHICH ROW THIS SITS ON. A row ENDS where the operator says it ends, whether or
   * not it is full — which is the whole of what "group these two together" means and what a
   * single flowing grid cannot express.
   */
  row: z.number().int().min(1).max(DASHBOARD_MAX_ROWS),
  size: z.enum(TILE_SIZES),
  titleOverride: labelOverride(DASHBOARD_TILE_TITLE_MAX),
  drillLabelOverride: labelOverride(DASHBOARD_TILE_DRILL_LABEL_MAX),
  /**
   * ⚠️ [C1e §3] THE TRI-STATE. `''` IS ACCEPTED HERE AND REFUSED ONE LINE UP, deliberately.
   *
   * ⚠️ `.default(null)` IS NOT DECORATION — `shortcutTarget` below took exactly that shape
   * when [P6b.1] added a key to a schema whose existing senders knew nothing about it.
   * Without it every payload written before this pass is a 422. `''` survives it, because
   * `.default()` fires only on `undefined`.
   */
  hintOverride: suppressibleOverride(DASHBOARD_TILE_HINT_MAX).default(null),
  /**
   * ⚠️ [P6b.1] THE ONE PIECE OF A TILE'S DEFINITION A LAYOUT MAY SET, AND THE WIDENING IS
   * DELIBERATE. Everywhere else this pass says the registry is closed to the configurator.
   * A shortcut has no number and its whole content IS its destination, so a fixed target
   * would make it a hard-coded link rather than a configurable tile.
   *
   * ⚠️ CHOSEN FROM AN ENUM, NEVER TYPED — see `dashboardShortcut.constant.ts`. A list is what
   * makes the capability check possible at all.
   */
  shortcutTarget: z.enum(DASHBOARD_SHORTCUT_TARGETS).nullable().default(null),
});

export const saveDashboardLayoutSchema = z.object({
  /**
   * ⚠️ `.min(1)` IS §4a's REFUSAL, IN THE SIGNATURE.
   *
   * MEASURED against `resolveDashboardLayout`: a saved empty layout does NOT render blank —
   * that is unreachable. It does something worse. With nothing dismissed, step 3 overlays the
   * default placements and appends them straight back, so the operator watches the tiles they
   * removed reappear and reads it as a failed save; with everything dismissed, step 7 hands
   * them the three-tile `MINIMUM` floor they never chose. **The refusal is about honesty, not
   * about blankness**, and it is a schema constraint rather than a runtime `if` for
   * `purgePlan.util.ts`'s reason.
   */
  entries: z
    .array(dashboardLayoutEntryInputSchema)
    .min(1, 'A dashboard must hold at least one tile.')
    .max(DASHBOARD_MAX_PLACEMENTS)
    /*
     * ⚠️ [P6b.2] TWO DE-DUPLICATIONS, AND THEY ARE DIFFERENT RULES.
     *
     * No two placements may share an INSTANCE id — that is a corrupt payload, and the
     * resolver's own header names drag-to-reorder as exactly the writer that produces one
     * during a bad save. And no two may share a TILE id unless that tile is `repeatable`:
     * two copies of one number is a page that lies about how much is on it, while two doors
     * is the feature.
     */
    .refine(
      (rows) => new Set(rows.map((row) => row.instanceId)).size === rows.length,
      'Each tile placement needs its own identity.',
    )
    .refine((rows) => {
      const singletons = rows.filter((row) => !DASHBOARD_TILES[row.id].repeatable).map((row) => row.id);
      return new Set(singletons).size === singletons.length;
    }, 'That tile may appear on a layout only once.')
    /*
     * ⚠️ A SHORTCUT ON A LAYOUT MUST POINT SOMEWHERE, AND IT IS REFUSED RATHER THAN QUIETLY
     * DROPPED. The resolver drops a targetless shortcut — it must, or a card that goes nowhere
     * reaches an operator — so without this refusal a super admin could add one, save, and
     * watch it vanish with nothing on screen to say why.
     */
    .refine(
      (rows) => rows.every((row) => !DASHBOARD_TILES[row.id].repeatable || row.shortcutTarget !== null),
      'A shortcut tile needs a destination before it can be saved.',
    ),
  locale: z.enum(LOCALES).default(DEFAULT_LOCALE),
});

export type SaveDashboardLayoutInput = z.infer<typeof saveDashboardLayoutSchema>;

// ── The read ─────────────────────────────────────────────────────────────────────

/**
 * Where one tile stands on one layout.
 *
 * ⚠️ `UNDECIDED` IS NOT `DISMISSED`, AND THE WHOLE OF §5 RESTS ON THE DIFFERENCE.
 * `dashboardLayout.model.ts` records it: without an explicit exclusion column, "absent from
 * `tile_ids`" would have to mean both "not yet arranged" and "taken away", and a resolver
 * cannot tell those apart. `UNDECIDED` is the set step 3 auto-appends — so a tile shipped in
 * a later release lands on a configured layout AND is markable here, from one storage fact.
 */
export const DASHBOARD_LAYOUT_ENTRY_STATES = ['ON', 'DISMISSED', 'UNDECIDED', 'UNAVAILABLE'] as const;
export type DashboardLayoutEntryState = (typeof DASHBOARD_LAYOUT_ENTRY_STATES)[number];

/**
 * ⚠️ A CODE, NOT A SENTENCE, BECAUSE THIS READER IS NOT THE TILE'S AUDIENCE.
 * `unavailableReason` on `dashboard.schema.ts` carries a sentence written for a VIEWER — "Your
 * own progress is on the unnamed-lines tile" — which is simply wrong when a super admin is
 * arranging somebody else's division dashboard. The configurator gets the code and says its
 * own thing; the viewer's sentence travels beside it, so a super admin can see what the
 * person whose screen this is would read in its place.
 */
export const DASHBOARD_TILE_UNAVAILABLE_REASONS = ['OUT_OF_SCOPE', 'CAPABILITY'] as const;
export type DashboardTileUnavailableReason = (typeof DASHBOARD_TILE_UNAVAILABLE_REASONS)[number];

export const dashboardLayoutConfigEntrySchema = z.object({
  id: z.enum(DASHBOARD_TILE_IDS),
  /** ⚠️ [P6b.2] `null` on a catalogue row that is not placed — see `state`. */
  instanceId: z.string().nullable(),
  row: z.number().int().min(1).nullable(),
  repeatable: z.boolean(),
  state: z.enum(DASHBOARD_LAYOUT_ENTRY_STATES),
  /** RESOLVED — what the row says, else the catalogue's own. */
  size: z.enum(TILE_SIZES),
  titleOverride: z.string().nullable(),
  drillLabelOverride: z.string().nullable(),
  /** ⚠️ [C1e §3] `''` here MEANS "the line is hidden" — see the input schema's builders. */
  hintOverride: z.string().nullable(),
  /** ⚠️ [P6b.1] Where a shortcut points; `null` on every tile that is not one. */
  shortcutTarget: z.enum(DASHBOARD_SHORTCUT_TARGETS).nullable(),
  /** Whether the CATALOGUE puts this tile on a fresh dashboard for this role. */
  defaultOn: z.boolean(),
  unavailableReason: z.enum(DASHBOARD_TILE_UNAVAILABLE_REASONS).nullable(),
  /** The sentence the TARGET would read in the tile's place. Null when it is available. */
  viewerReason: z.string().nullable(),
});

/**
 * ⚠️ A NAME AND AN ID, NEVER `officerSummarySchema`. That DTO carries a phone number, and §10
 * is a data-minimisation rule as much as an authorisation one — `policyVersionSchema`'s
 * `publishedByName` set this precedent for exactly the same "who did this" question.
 */
const editedBySchema = {
  updatedByName: z.string().nullable(),
  updatedByOfficerId: objectIdSchema.nullable(),
  updatedAt: z.iso.datetime().nullable(),
};

export const dashboardLayoutSummarySchema = z.object({
  accessRole: z.enum(DASHBOARD_ACCESS_ROLES),
  orgUnitType: z.enum(ORG_UNIT_TYPES),
  layoutSource: z.enum(LAYOUT_RESOLUTION_SOURCES),
  tileCount: z.number().int().nonnegative(),
  /**
   * ⚠️ "A PERSON HAS ARRANGED THIS", AND IT IS WHAT MAKES §5's "NEW" MEAN ANYTHING.
   * On a freshly seeded row every analytical tile is in neither list, so "undecided" is true
   * of eight tiles on a system nobody has touched — a badge on eight of fourteen says
   * nothing. `updated_by_officer_id` is `null` until P6b's write path sets it, so before the
   * first edit the screen says "available" and after it says "new".
   */
  configured: z.boolean(),
  ...editedBySchema,
});

/**
 * ⚠️ TWELVE ROWS, BOUNDED AND UNPAGINATED — §7's `GET /org-units/counts` exception verbatim.
 * The bound is `DASHBOARD_ACCESS_ROLES.length × ORG_UNIT_TYPES.length`, a SERVER constant that
 * cannot be raised by a caller, and a page of it is not a coherent answer: the screen renders
 * a matrix, so page two arriving late would draw a grid with holes in it.
 */
export const dashboardLayoutIndexSchema = z.object({
  items: z.array(dashboardLayoutSummarySchema),
});

export const dashboardLayoutConfigSchema = z.object({
  target: dashboardLayoutTargetSchema,
  locale: z.enum(LOCALES),
  layoutSource: z.enum(LAYOUT_RESOLUTION_SOURCES),
  configured: z.boolean(),
  ...editedBySchema,
  /**
   * ⚠️ ONE ARRAY OF FOURTEEN, WITH THE `ON` TILES FIRST IN THEIR RESOLVED ORDER.
   *
   * The drag list is then `entries.filter(e => e.state === 'ON')` IN PLACE — never a
   * client-side sort, which would be a second expression of the order the resolver already
   * decided and would disagree with the dashboard the first time the two drifted.
   *
   * ⚠️ AND THE UNAVAILABLE ONES ARE HERE RATHER THAN OMITTED. `dashboard.schema.ts` already
   * makes this argument for the viewer's payload — "a tile that is silently absent looks like
   * a bug" — and it is stronger for a configurator: §2c requires the reason to be readable,
   * and a payload that omits the tile leaves the screen nothing to attach it to.
   */
  entries: z.array(dashboardLayoutConfigEntrySchema),
});
